import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import FeatureName, { DefaultOnFeatures } from 'src/constants/featureName';
import { LOCAL_STORAGE_PREFIX } from '@constants/strings';

interface FeatureFlagContextType {
  isFeatureOn: (featureName: FeatureName) => boolean;
  setFeatureStatus: (featureName: FeatureName, status: boolean) => void;
  resetFeatureStatus: (featureName: FeatureName) => void;
  setAllFeaturesOn: () => void;
  setAllFeaturesOff: () => void;
  setAllFeaturesDefault: () => void;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

function localStorageKeyName(featureName: FeatureName) {
  return `${LOCAL_STORAGE_PREFIX}${featureName.toString()}`;
}

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  // Force re-render when feature flags change
  const [, forceUpdate] = useState({});
  const triggerUpdate = useCallback(() => forceUpdate({}), []);

  const isFeatureOn = useCallback((featureName: FeatureName): boolean => {
    const featureStatus = localStorage.getItem(localStorageKeyName(featureName));

    if (featureStatus == null) {
      return DefaultOnFeatures.includes(featureName);
    }
    return featureStatus === String(true);
  }, []);

  const setFeatureStatus = useCallback(
    (featureName: FeatureName, status: boolean) => {
      localStorage.setItem(localStorageKeyName(featureName), String(status));
      triggerUpdate();
    },
    [triggerUpdate],
  );

  const resetFeatureStatus = useCallback(
    (featureName: FeatureName) => {
      localStorage.removeItem(localStorageKeyName(featureName));
      triggerUpdate();
    },
    [triggerUpdate],
  );

  const setAllFeaturesOn = useCallback(() => {
    Object.keys(FeatureName).forEach((featureNameEnumKey) => {
      const featureName = FeatureName[featureNameEnumKey as keyof typeof FeatureName] as FeatureName;
      localStorage.setItem(localStorageKeyName(featureName), String(true));
    });
    triggerUpdate();
  }, [triggerUpdate]);

  const setAllFeaturesOff = useCallback(() => {
    Object.keys(FeatureName).forEach((featureNameEnumKey) => {
      const featureName = FeatureName[featureNameEnumKey as keyof typeof FeatureName] as FeatureName;
      localStorage.setItem(localStorageKeyName(featureName), String(false));
    });
    triggerUpdate();
  }, [triggerUpdate]);

  const setAllFeaturesDefault = useCallback(() => {
    Object.keys(FeatureName).forEach((featureNameEnumKey) => {
      const featureName = FeatureName[featureNameEnumKey as keyof typeof FeatureName] as FeatureName;
      localStorage.removeItem(localStorageKeyName(featureName));
    });
    triggerUpdate();
  }, [triggerUpdate]);

  const value: FeatureFlagContextType = {
    isFeatureOn,
    setFeatureStatus,
    resetFeatureStatus,
    setAllFeaturesOn,
    setAllFeaturesOff,
    setAllFeaturesDefault,
  };

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export function useFeatureFlagContext(): FeatureFlagContextType {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlagContext must be used within a FeatureFlagProvider');
  }
  return context;
}
